// U.GG appends these to the iframe URL. The assertion is HMAC-signed with their private
// partner key; we never verify it, the partner API does. Docs: developer portal,
// "Signed iframe Requests (Signed Assertions)".

export type TUggState =
  | "ready"
  | "multi"
  | "none"
  | "create"
  | "signedout"
  | "expired"
  | "capped"
  | "ratelimited"
  | "generating"
  | "unsupported";

export interface IUggContext {
  riotID: string | null;
  matchId: string | null;
  region: string | null;
  assertion: string | null;
  loginUrl: string | null;
  forcedState: TUggState | null;
}

const FORCEABLE_STATES: TUggState[] = [
  "ready",
  "multi",
  "none",
  "create",
  "signedout",
  "expired",
  "capped",
  "ratelimited",
  "generating",
  "unsupported",
];

const readText = (value: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

// loginUrl is unsigned (the signed-out screen has no assertion by definition), so anyone
// can embed us with one. https u.gg only, or it becomes an open redirect.
const LOGIN_HOST = "u.gg";

// ponytail: hardcoded until U.GG confirms their login URL; ?loginUrl= overrides it.
const DEFAULT_LOGIN_URL = "https://u.gg/login";

const readLoginUrl = (value: string | null): string | null => {
  const raw = value?.trim();
  if (!raw) return null;

  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return null;
    const host = url.hostname.toLowerCase();
    if (host !== LOGIN_HOST && !host.endsWith(`.${LOGIN_HOST}`)) return null;
    return url.toString();
  } catch {
    return null;
  }
};

// The `e` claim, unverified: it only picks a message. The server still decides.
export const isAssertionExpired = ({ assertion }: IUggContext): boolean => {
  const payload = assertion?.split(".")[0];
  if (!payload) return false;

  try {
    const { e } = JSON.parse(
      window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof e === "number" && e * 1000 <= Date.now();
  } catch {
    return false;
  }
};

const readForcedState = (value: string | null): TUggState | null => {
  const candidate = value?.trim().toLowerCase();
  return FORCEABLE_STATES.find((state) => state === candidate) ?? null;
};

export const readUggContext = (search: string): IUggContext => {
  const params = new URLSearchParams(search);

  return {
    riotID: readText(params.get("riotID")),
    matchId: readText(params.get("matchId")),
    region: readText(params.get("region")),
    assertion: readText(params.get("assertion")),
    loginUrl: readLoginUrl(params.get("loginUrl")) ?? DEFAULT_LOGIN_URL,
    forcedState: readForcedState(params.get("state")),
  };
};

export const canCreate = ({ assertion }: IUggContext): boolean =>
  Boolean(assertion);

export const hasCreateInputs = ({ riotID, matchId, region }: IUggContext) =>
  Boolean(riotID && matchId && region);

/**
 * 201 renders, 200 is a dry run that never produces a clip, 204 means one already exists.
 * Assertion failures carry a `code`; a bare 400 is either "no best play in this match" or
 * a body we got wrong, and only the message separates them.
 */
export const mapCreateResponse = (
  status: number,
  code?: string,
  message?: string,
): TUggState => {
  if (status === 200 || status === 201 || status === 204) return "generating";

  if (status === 429) {
    if (code === "QUOTA_EXCEEDED") return "capped";
    // Unknown 429s read as transient rather than wrongly demanding payment.
    return "ratelimited";
  }

  if (status === 401 || status === 403) {
    return code === "ASSERTION_EXPIRED" ? "expired" : "signedout";
  }
  if (status === 400) {
    if (code) return "signedout";
    return /best play/i.test(message || "") ? "none" : "unsupported";
  }

  return "unsupported";
};
